// Lightweight, client-side resume-quality heuristics. No AI calls, no
// backend changes — these run instantly as the user types, and the
// "Improve"/"Tailor now" actions hand off to the real AI (via the shared
// OmniAgent chat) for anything that actually needs judgment.

import { matchesSkillInCorpus } from './skillGraph';

// HTML entities, decoded in the one order that round trips.
//
// &amp; MUST come last. Decoding it first turns "&amp;lt;" into "&lt;", which
// the next rule then turns into "<" — so a bullet a user typed as "&lt;script&gt;"
// came back as "<script>" and stayed that way. Confirmed by round tripping it
// through serializeBulletsToHtml and back: one edit and the original text was
// gone for good.
//
// &quot; and &#39; are here because rich text editors and imported resumes emit
// them for ordinary quotes and apostrophes, and without them a bullet reading
// "Owned the team's roadmap" displayed the raw "&#39;" to the user.
const decodeEntities = (str) =>
  String(str)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');

// The nullish guard is explicit rather than a default parameter. A default only
// fires for undefined, so stripHtml(null) fell through to String(null) and put
// the literal word "null" on screen wherever a bullet was missing.
export const stripHtml = (str) => {
  if (str === null || str === undefined) return '';
  return decodeEntities(String(str).replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
};

const escapeHtml = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Extracts individual bullet strings from the "<ul><li>...</li></ul>" HTML
// blob the editor stores. Falls back to newline-split plain text for older
// entries that predate the <ul> format, so nothing already saved breaks.
export const parseBulletsFromHtml = (html = '') => {
  if (!html || !html.trim()) return [];
  const liMatches = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)];
  if (liMatches.length > 0) {
    return liMatches.map(m => stripHtml(m[1])).filter(Boolean);
  }
  // Split BEFORE cleaning, and treat bullet glyphs as separators.
  //
  // Text arrives from three places and only one of them uses <li>: the editor
  // writes markup, the assistant returns plain lines, and anything pasted from
  // a PDF or Word carries literal bullet characters. Splitting only on newlines
  // left "• A • B" as a single bullet with the glyphs still embedded, which is
  // what broke the batch-update diff view.
  //
  // stripHtml collapses all whitespace, newlines included, so splitting its
  // output on \n could never return more than one item — this fallback looked
  // like it handled pasted multi-line text and silently joined it into a single
  // bullet instead. <br> counts as a line break here for the same reason: it is
  // what a contenteditable produces when someone presses return.
  return String(html)
    .split(/<br\s*\/?>|\r?\n|[\u2022\u2023\u25AA\u25CF\u00B7]/i)
    .map((line) => stripHtml(line))
    .filter(Boolean);
};

// Inverse of parseBulletsFromHtml — always serializes back to the <ul><li>
// format the PDF generator (generate-pdf.js) expects, regardless of what
// format the original data was in.
export const serializeBulletsToHtml = (bullets = []) => {
  const clean = bullets.map(b => b.trim()).filter(Boolean);
  if (clean.length === 0) return '';
  return `<ul>${clean.map(b => `<li>${escapeHtml(b)}</li>`).join('')}</ul>`;
};

const WEAK_STARTERS = [
  'responsible for', 'worked on', 'worked with', 'helped', 'helped with',
  'duties included', 'in charge of', 'assisted', 'assisted with',
  'tasked with', 'involved in', 'participated in', 'was ', 'were ',
  'did ', 'handled', 'in charge', 'my job was'
];

const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'you', 'your', 'will', 'are', 'our', 'have',
  'this', 'that', 'from', 'who', 'can', 'all', 'job', 'role', 'team', 'work',
  'skills', 'experience', 'ability', 'strong', 'excellent', 'including',
  'etc', 'a', 'an', 'to', 'of', 'in', 'on', 'is', 'as', 'be', 'or', 'we',
  'us', 'their', 'they', 'it', 'its', 'at', 'by', 'across', 'more', 'other',
  'such', 'not', 'but', 'into', 'within', 'per', 'about', 'than', 'also',
  'while', 'both', 'each', 'these', 'those', 'any', 'may', 'must', 'should',
  'looking', 'seeking', 'candidate', 'candidates', 'position', 'company',
  'years', 'year', 'plus', 'preferred', 'required', 'responsibilities',
  'qualifications', 'requirements', 'duties', 'ideal', 'join', 'help',
  'proficiency', 'proficient', 'knowledge', 'understanding', 'familiarity',
  'working', 'closely', 'daily', 'environment', 'fast-paced', 'opportunity',
  'experienced', 'proven', 'successful', 'passionate', 'track', 'record', 'background'
]);

const MULTI_WORD_PATTERNS = [
  'distributed systems', 'system design', 'machine learning', 'artificial intelligence',
  'prompt engineering', 'continuous integration', 'continuous deployment', 'ci/cd',
  'object-oriented programming', 'test-driven development', 'rest api', 'restful api',
  'restful apis', 'microservices architecture', 'event-driven architecture',
  'data engineering', 'data pipelines', 'cloud computing', 'amazon web services',
  'google cloud platform', 'microsoft azure', 'infrastructure as code',
  'react native', 'tailwind css', 'full stack', 'front end', 'back end'
];

// ── What counts as a number that MEANS something ────────────────────────────
//
// The first version of this asked /\d/ — any digit at all. That marked
// "Migrated to Java 8", "Built the v2 API" and "Worked there since 2019" as
// quantified, so bullets with no measurable outcome were labelled strong and
// the user was told nothing needed fixing. A diagnostic that lies is worse than
// no diagnostic: it actively stops someone asking for help.
//
// A metric is a number attached to a magnitude. A version or a year is a
// number attached to a name.
const METRIC_PATTERNS = [
  /\d\s*%/,                                   // 45%, 45 %
  /[$£€]\s*\d/,                                // $2.3M
  /\d+(\.\d+)?\s*[x×]\b/i,                    // 3x, 2.5x
  /\b\d{1,3}(,\d{3})+\b/,                     // 1,200,000
  /\b\d+(\.\d+)?\s*(k|m|bn|b)\b/i,            // 10k, 4M
  /\b(by|to|from|over|under|across|of)\s+\d/i, // "by 45", "team of 4"
  /\d+\s*(hours?|days?|weeks?|months?|minutes?|seconds?|ms)\b/i,
  /\d+\+/,                                    // 40+
];

// No stripping pass. An earlier version removed version numbers and years
// first, using a "word followed by a number" pattern — which also matched
// "by 45", "of 4" and "to 90", so the very phrases that signal a metric were
// deleted before the test ran. The patterns above are specific enough on their
// own: a year carries no percent sign, scale suffix, thousands separator or
// impact preposition, so it cannot match any of them.
export const hasRealMetric = (text) =>
  METRIC_PATTERNS.some((re) => re.test(String(text || '')));

/**
 * One diagnosis per bullet, in the order a recruiter notices problems.
 *
 * This is the funnel into the assistant, not decoration. The label is what the
 * user clicks, so it has to name the problem in words they already know — and
 * `action` is the fix the chat is asked for when they do.
 *
 * A weak opening outranks a missing number: a line starting "Responsible for"
 * reads as passive no matter what figures follow it.
 */
export const scoreBullet = (rawText) => {
  const text = stripHtml(rawText).trim();
  if (!text) return { tag: null, label: null, action: null, hint: null };

  const lower = text.toLowerCase();

  if (WEAK_STARTERS.some((w) => lower.startsWith(w))) {
    return {
      tag: 'weak-verb',
      label: 'starts weak',
      action: 'verb',
      hint: 'Opens with a phrase about your duties. Lead with what you did.',
    };
  }

  if (!hasRealMetric(text)) {
    return {
      tag: 'no-metric',
      label: 'no numbers',
      action: 'xyz',
      hint: 'No measurable result. Add a figure showing the impact.',
    };
  }

  if (text.length > 220) {
    return {
      tag: 'too-long',
      label: 'too long',
      action: 'tighten',
      hint: 'Long enough that a skim will miss the point.',
    };
  }

  return { tag: 'strong', label: 'strong', action: null, hint: 'Leads with impact and quantifies it.' };
};

export const extractKeywordsFromJd = (jdText = '') => {
  if (!jdText || !jdText.trim()) return [];

  const jdLower = ` ${jdText.toLowerCase()} `;
  const extracted = new Map();

  for (const phrase of MULTI_WORD_PATTERNS) {
    if (jdLower.includes(` ${phrase} `) || jdLower.includes(phrase)) {
      extracted.set(phrase, (extracted.get(phrase) || 0) + 2);
    }
  }

  const tokens = (jdText.toLowerCase().match(/[a-z0-9][a-z0-9+#./-]{1,}/g) || [])
    .map(t => t.replace(/[.,;:]+$/, ''))
    .filter(t => t.length >= 2 && !STOPWORDS.has(t) && !/^\d+$/.test(t));

  for (const token of tokens) {
    const isPartOfMultiWord = Array.from(extracted.keys()).some(phrase => phrase.includes(token));
    if (!isPartOfMultiWord) {
      extracted.set(token, (extracted.get(token) || 0) + 1);
    }
  }

  return [...extracted.entries()].sort((a, b) => b[1] - a[1]);
};

// Fast, grounded keyword-overlap match score.
// Evaluates candidate resume against JD keywords using synonym graph & fuzzy matching.
export const computeMatchScore = (resumeText = '', jdText = '') => {
  const rankedKeywords = extractKeywordsFromJd(jdText);
  if (rankedKeywords.length === 0) return null;

  const found = [];
  const missing = [];
  let weightedEarned = 0;
  let weightedTotal = 0;

  for (const [keyword, weight] of rankedKeywords) {
    weightedTotal += weight;
    const isMatched = matchesSkillInCorpus(keyword, resumeText);

    if (isMatched) {
      weightedEarned += weight;
      found.push(keyword);
    } else {
      missing.push(keyword);
    }
  }

  const total = rankedKeywords.length;
  const score = weightedTotal === 0 ? 0 : Math.min(100, Math.round((weightedEarned / weightedTotal) * 100));

  return {
    score,
    found: found.slice(0, 8),
    missing: missing.slice(0, 8),
    totalKeywords: total
  };
};
